import { useEffect } from 'react';

import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import useAuth from '@/hooks/useAuth';
import {
  useCategoryLabels,
  useCategoryLabelsGrouped,
} from '@/hooks/useCategoryLabels';
import { useFlags } from '@/hooks/useFlags';
import { useScrollMemory } from '@/hooks/useScrollMemory/useScrollMemory';
import { useSortOptions } from '@/hooks/useSortOptions';

import { useFeaturePermission } from '@/utils/permissions';
import { useMultipleReportsTabsEnabled } from '@/utils/reports';
import { withRoles } from '@/utils/withRoles';

import Filter, {
  FilterCheckboxSectionProps,
  FilterSearchBarProps,
} from '@/components/Filter';
import { FilterAndSortButtonProps } from '@/components/Filter/components/FilterAndSortButton';
import FilterDesktop from '@/components/Filter/components/FilterDesktop';
import FilterMobile from '@/components/Filter/components/FilterMobile';
import { FilterLayoutProps } from '@/components/Filter/types';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import SortFilterHeader from '@/components/SortFilterHeader/SortFilterHeader';

import { LicenseeLocationTrigger } from './LicenseeLocationTrigger';
import PlansList from './PlansList';

import {
  DataViewAtomFamilyKey,
  sourceDataFamily,
  useDataView,
} from '@/atoms/data-view';
import {
  type ReportsPlanRow,
  useGetPlansSummary,
} from '@/routes/Reports/hooks/useGetPlansSummary';
import { useReportsPlansFocus } from '@/routes/Reports/state';
import ToggleButtons from '@/routes/Reports/ToggleButtons';

const ReportsPlansTab = () => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();
  const { planDescriptions: planDescriptionsFF } = useFlags();
  const isOperatorConsultantUser = auth?.isOperatorConsultant ?? false;
  const shouldSeeCompliance = useFeaturePermission('reporting.compliance');

  const [isLicenseeMode, setIsLicenseeMode] =
    useReportsPlansFocus('licenseeModeToggle');

  const { data: plans = [], isFetching: isFetchingPlans } =
    useGetPlansSummary();

  const dataViewKey = isLicenseeMode
    ? DataViewAtomFamilyKey.REPORTS_PLANS_LICENSEE
    : DataViewAtomFamilyKey.REPORTS_PLANS;

  const setSource = useSetAtom(sourceDataFamily(dataViewKey));

  useEffect(() => {
    if (!isFetchingPlans) {
      setSource(plans);
    }
  }, [isFetchingPlans, plans, setSource]);

  const [
    { display, filters, sort },
    {
      setSort,
      toggleCheckboxFilter,
      setSearch,
      resetSearch,
      resetAllCheckboxFilters,
    },
  ] = useDataView<ReportsPlanRow>(dataViewKey);

  const currentFilters = Object.values(filters.in).flat();

  const categoriesValues = Object.values(filters.in.category ?? []);

  const { total, items } = display;

  const sortOptions = useSortOptions({ reportsPlansView: true });

  const userIsLicenseeStaff = auth?.userIsLicenseeStaff ?? false;

  useScrollMemory({
    container: 'body',
    key: dataViewKey,
    enabled: !isFetchingPlans,
  });

  const filterAndSortButtonOptions: FilterAndSortButtonProps = {
    onSortChange: setSort,
    sortOptions,
    sortValue: sortOptions.find(
      option =>
        option.key === sort?.key && option.direction === sort?.direction,
    ),
    text: `${t('TrainingPlans.filtering.show')} ${
      items.length
    } ${t('TrainingPlans.filtering.results')}`,
  };

  const userLicenseeLocations = auth?.licenseeLocations ?? [];

  const categoryLabelsNoCompliance = useCategoryLabels(categoriesValues, true);
  const categoryLablesCompliance = useCategoryLabelsGrouped(categoriesValues);

  const tabsActive = useMultipleReportsTabsEnabled();

  const locationsOptions: FilterCheckboxSectionProps = {
    title: t('Licensee.licenseeLocations'),
    labels: userLicenseeLocations.reduce<
      Record<string, { translationString: string; value: boolean }>
    >((acc, location) => {
      acc = {
        ...acc,
        [location.number ?? '']: {
          translationString: location.name ?? '',
          value: currentFilters.includes(location.number ?? ''),
        },
      };
      return acc;
    }, {}),
    onToggle: value => toggleCheckboxFilter('ownerId', value),
  };

  const categoriesOptions = {
    title: t('Browse.categories'),
    onChange: (key: string) => {
      toggleCheckboxFilter('category', key);
    },
  };

  const plansOptions: FilterSearchBarProps = {
    onChange: e => {
      setSearch(
        e.target.value,
        planDescriptionsFF
          ? ['nameLocalLang', 'descriptionSearch']
          : 'nameLocalLang',
      );
    },
    onClear: () => resetSearch(),
    placeholder: t('Reports.filtering.searchPlans'),
    value: filters.search,
  };

  let filterContent: FilterLayoutProps | null;

  if (userIsLicenseeStaff) {
    // Brand Champion.
    filterContent = {
      before: (
        <>
          <LicenseeLocationTrigger
            className="hidden md:block"
            wrapFilterSurface
          />
          <LicenseeLocationTrigger className="block md:hidden" />
        </>
      ),
      search: <Filter.SearchBar {...plansOptions} />,
      children: (
        <Filter.CheckboxSection
          {...categoriesOptions}
          labels={categoryLabelsNoCompliance}
        />
      ),
    };
  } else if (isOperatorConsultantUser) {
    // Operator Consultant
    filterContent = {
      before: (
        <>
          <ToggleButtons
            dataTestId="licensee-toggle-buttons"
            isLicenseeMode={isLicenseeMode}
            setIsLicenseeMode={setIsLicenseeMode}
          />
        </>
      ),
      children: (
        <>
          {isLicenseeMode && (
            <>
              <Filter.CheckboxSection {...locationsOptions} />
              <Filter.Divider />
            </>
          )}
          <Filter.CheckboxSection
            {...categoriesOptions}
            labels={
              isLicenseeMode
                ? categoryLabelsNoCompliance
                : categoryLablesCompliance
            }
          />
        </>
      ),
      search: <Filter.SearchBar {...plansOptions} />,
    };
  } else {
    // Default
    filterContent = {
      search: <Filter.SearchBar {...plansOptions} />,
      children: (
        <Filter.CheckboxSection
          {...categoriesOptions}
          labels={
            // Cam Korver is a multi-licensee user with no franchises and shoudln't see Compliance
            shouldSeeCompliance
              ? categoryLablesCompliance
              : categoryLabelsNoCompliance
          }
        />
      ),
    };
  }

  return (
    <>
      <div className="m-w-full relative flex grow flex-col">
        <LoadingOverlay isOpen={isFetchingPlans} />
        {!isFetchingPlans && (
          <div className="flex w-full flex-col">
            <FilterMobile
              {...{ ...filterContent, filterAndSortButtonOptions }}
            />
            <div className="flex w-full justify-end md:pl-[304px]">
              <SortFilterHeader
                label={t('TrainingPlans.filtering.sortBy')}
                onChange={setSort}
                onClear={() => {
                  resetAllCheckboxFilters();
                }}
                options={sortOptions}
                showClear={false}
                showCompletedPlansOption={false}
                showMyCompletedPlansOnlySwitchValue={false}
                text={`${total} ${t('Reports.tabPlans')}`}
                title={t('Reports.tabPlans')}
                total={total}
                value={sortOptions.find(
                  option =>
                    option.key === sort?.key &&
                    option.direction === sort?.direction,
                )}
              />
            </div>
            {/* Bottom Row */}
            <div className="mt-2.5 flex justify-between">
              {/* Left Side */}
              <FilterDesktop
                {...{ ...filterContent, filterAndSortButtonOptions }}
                tabsActive={tabsActive}
              />
              {/* Right Side */}
              <div className="w-full">
                <div className="relative flex grow gap-6">
                  <PlansList
                    isFetching={isFetchingPlans}
                    dataViewKey={
                      isLicenseeMode
                        ? 'reports-plans-licensee'
                        : 'reports-plans'
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const ReportsPlansTabWithRoles = withRoles(ReportsPlansTab, 'reporting.plans');

export default ReportsPlansTabWithRoles;
